import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useAuthUserRestaurant,
  useGetSuppliersQuery,
} from '@durma-soft/gros-sdk';
import { ProductOfferCurrency } from '@durma-soft/gros-sdk/dist/types/product-offers';

import { Input } from '@/components/shared/shadcn-ui/input';
import { Label } from '@/components/shared/shadcn-ui/label';
import { CustomFileInput } from '@/components/shared/custom-file-input';
import { CreateSupplierModal } from '@/components/restaurant/suppliers/create-supplier-modal';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/shared/shadcn-ui/select';

export interface ProductOfferFormData {
  name: string;
  supplier_id: number | null;
  price_per_unit: number | null;
  unit: string;
  currency: ProductOfferCurrency;
  // string = existing image URL, File = new upload, null = no image / removed
  image: string | File | null;
}

export const CURRENCIES: ProductOfferCurrency[] = ['BAM', 'EUR', 'RSD'];

// Sentinel Select value that opens the create-supplier modal instead of selecting.
const CREATE_SUPPLIER_OPTION = '__create-supplier__';

interface ProductOfferFormFieldsProps {
  formData: ProductOfferFormData;
  setFormData: (formData: ProductOfferFormData) => void;
}

export const ProductOfferFormFields = ({
  formData,
  setFormData,
}: ProductOfferFormFieldsProps) => {
  const { t } = useTranslation();
  const uid = useId();

  const restaurant = useAuthUserRestaurant();
  const suppliers = useGetSuppliersQuery({ restaurant_id: restaurant.id });
  const [isCreateSupplierOpen, setIsCreateSupplierOpen] = useState(false);

  return (
    <div className="flex flex-col gap-4 p-2">
      <div className="flex flex-col gap-2">
        <Label htmlFor={uid + '-name'}>{t('common.name')}</Label>
        <Input
          required
          id={uid + '-name'}
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
        />
      </div>
      <div className="flex flex-col gap-2">
        <Label htmlFor={uid + '-supplier'}>
          {t('product-offers.supplier')}
        </Label>
        <Select
          value={formData.supplier_id ? String(formData.supplier_id) : ''}
          onValueChange={(value) => {
            if (value === CREATE_SUPPLIER_OPTION) {
              setIsCreateSupplierOpen(true);
              return;
            }
            setFormData({ ...formData, supplier_id: Number(value) });
          }}
        >
          <SelectTrigger id={uid + '-supplier'}>
            <SelectValue placeholder={t('suppliers.select-supplier')} />
          </SelectTrigger>
          <SelectContent>
            {(suppliers.data ?? []).map((supplier) => (
              <SelectItem key={supplier.id} value={String(supplier.id)}>
                {supplier.name}
              </SelectItem>
            ))}
            <SelectItem value={CREATE_SUPPLIER_OPTION}>
              + {t('suppliers.create')}
            </SelectItem>
          </SelectContent>
        </Select>
        <CreateSupplierModal
          isOpen={isCreateSupplierOpen}
          onClose={() => setIsCreateSupplierOpen(false)}
          onCreated={(supplier) =>
            setFormData({ ...formData, supplier_id: supplier.id })
          }
        />
      </div>
      <div className="flex flex-col gap-2">
        <Label htmlFor={uid + '-price'}>
          {t('product-offers.price-per-unit')}
        </Label>
        <Input
          required
          id={uid + '-price'}
          min={0.01}
          step="any"
          type="number"
          inputMode="decimal"
          value={formData.price_per_unit ?? ''}
          onChange={(e) =>
            setFormData({
              ...formData,
              price_per_unit: e.target.value === '' ? null : +e.target.value,
            })
          }
        />
      </div>
      <div className="flex flex-col gap-2">
        <Label htmlFor={uid + '-unit'}>{t('product-offers.unit')}</Label>
        <Input
          required
          id={uid + '-unit'}
          value={formData.unit}
          placeholder="kg / kom / l"
          onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
        />
      </div>
      <div className="flex flex-col gap-2">
        <Label htmlFor={uid + '-currency'}>
          {t('product-offers.currency')}
        </Label>
        <Select
          value={formData.currency}
          onValueChange={(value) =>
            setFormData({
              ...formData,
              currency: value as ProductOfferCurrency,
            })
          }
        >
          <SelectTrigger id={uid + '-currency'}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CURRENCIES.map((currency) => (
              <SelectItem key={currency} value={currency}>
                {currency}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex flex-col gap-2">
        <CustomFileInput
          label={t('common.add-image')}
          accept="image/png,image/jpeg"
          hasDeleteButton={Boolean(formData.image)}
          onDelete={() => setFormData({ ...formData, image: null })}
          handleFile={(image) => setFormData({ ...formData, image })}
        />
        <div>
          {formData.image && (
            <img
              src={
                typeof formData.image === 'string'
                  ? formData.image
                  : URL.createObjectURL(formData.image)
              }
              height="128"
              width="128"
              className="object-contain object-center w-32 h-32 rounded-md"
            />
          )}
        </div>
      </div>
    </div>
  );
};
